import React, { useMemo, useState } from "react";
import useSWR from "swr";
import useAuth from "../../../../../hooks/useAuth";
import { deleteFetcher, getFetcher, postFetcher, putFetcher } from "../../../../../services/api";
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure } from "@nextui-org/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ControlledInput from "../../../../elements/ControlledInput";
import useListCompany from "../../../../../hooks/useListCompany";
import { useSelectedCompany } from "../../../../../hooks/useSelectedCompany";
import ControlledReactSelect from "../../../../elements/ControlledReactSelect";
import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import useBadRequestForm from "../../../../../hooks/useBadRequestForm";
import { picValidation } from "../../../../../services/validation/pic.validation";
import ClientDataGrid from "../../../../elements/ClientDataGrid";
import ModalAlert from "../../../../elements/ModalAlert";
import FilterReactSelect from "../../../../elements/FilterReactSelect";

export default function TablePic() {
  const [editId, setEditId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  // Perusahaan mengikuti pengguna yang sedang login; super admin tetap bebas memilih.
  const [selectedCompany, setSelectedCompany] = useSelectedCompany();
  const { user, isSuperAdmin } = useAuth();
  const { formErrorMessage, isResponseErrorObject, setFormErrorMessage } = useBadRequestForm();
  const { isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm } = useDisclosure();
  const { isOpen: isOpenModalAlert, onOpenChange: onOpenChangeModalAlert } = useDisclosure();
  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting },
  } = useForm({ resolver: yupResolver(picValidation) });
  const { dataCompany, isLoadingCompany } = useListCompany();
  const { data, isLoading, mutate } = useSWR(
    isSuperAdmin
      ? `/api/company/search-data-pic?returnAll=true&sortOrder=desc${selectedCompany ? `&companyIds=${selectedCompany.value}` : ""}`
      : `/api/company/search-data-pic?companyIds=${selectedCompany ? selectedCompany.value : user.companies.map((company) => company.companyId).join(",")}&returnAll=true`,
    getFetcher
  );
  const columns = useMemo(
    () => [
      { field: "namaPIC", headerName: "Nama" },
      { field: "jabatan", headerName: "Jabatan" },
      { field: "email", headerName: "Email" },
      { field: "telepon", headerName: "Telepon" },
      { field: "fax", headerName: "Fax" },
      {
        field: "action",
        headerName: "Aksi",
        renderCell: (params) => (
          <div className="flex items-center gap-1">
            <Button size="sm" onPress={() => onClickEdit(params.row)} color="warning" isIconOnly>
              <PencilSquareIcon className="size-4" />
            </Button>
            <Button size="sm" onPress={() => onClickDelete(params.row.id)} color="danger" isIconOnly>
              <TrashIcon className="size-4" />
            </Button>
          </div>
        ),
        sortable: false,
        filterable: false,
      },
    ],
    [onClickEdit]
  );

  function onClickEdit(item) {
    setEditId(item.id);
    setIsEdit(true);
    reset({
      companyId: item.companyId,
      namaPIC: item.namaPIC,
      jabatan: item.jabatan,
      email: item.email,
      telepon: item.telepon,
      fax: item.fax,
    });
    onOpenChangeModalForm();
  }

  function onClickDelete(id) {
    setEditId(id);
    onOpenChangeModalAlert();
  }

  function onCloseForm() {
    setEditId(null);
    setIsEdit(false);
    reset({
      companyId: "",
      namaPIC: "",
      jabatan: "",
      email: "",
      telepon: "",
      fax: "",
    });
    onCloseModalForm();
  }

  async function onSubmitDelete() {
    try {
      await deleteFetcher("/api/company/delete-data-pic", editId);
      mutate();
      toast.success("Penanggung jawab berhasil dihapus!");
    } catch (error) {
      toast.error("Gagal hapus penanggung jawab!");
    }
  }

  async function onSubmitForm(data) {
    setFormErrorMessage(null);
    try {
      if (isEdit) {
        delete data.companyId;
        await putFetcher("/api/company/update-data-pic", editId, data);
        mutate();
        toast.success("Penanggung jawab berhasil diubah!");
      } else {
        await postFetcher("/api/company/create-data-pic", data);
        mutate();
        toast.success("Penanggung jawab berhasil ditambah!");
      }
      onCloseForm();
    } catch (error) {
      isResponseErrorObject(error.response.data);
    }
  }

  return (
    <>
      <div className="mb-5">
        <Button onPress={onOpenModalForm} size="sm" color="primary" startContent={<PlusIcon className="size-4 stroke-2" />}>
          Tambah
        </Button>
      </div>
      <div className="w-full h-[500px]">
        <div className="w-full flex justify-end mb-3">
          <div className="w-1/4">
            <FilterReactSelect defaultValue={selectedCompany} isLoading={isLoadingCompany} options={dataCompany.data?.map((item) => ({ label: item.name, value: item.id }))} placeholder="Pilih Perusahaan" setValue={setSelectedCompany} />
          </div>
        </div>
        <ClientDataGrid columns={columns} rows={data} isLoading={isLoading} />
      </div>

      <ModalAlert isOpen={isOpenModalAlert} onOpenChange={onOpenChangeModalAlert} onSubmit={onSubmitDelete} icon="danger" />
      <Modal scrollBehavior="inside" isOpen={isOpenModalForm} onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{isEdit ? "Ubah" : "Tambah"} Perusahaan</ModalHeader>
              <ModalBody>
                {formErrorMessage && (
                  <ul className="text-xs text-white bg-danger p-3">
                    {Object.entries(formErrorMessage).map(([key, value]) => (
                      <li key={key}>{value}</li>
                    ))}
                  </ul>
                )}
                <form onSubmit={handleSubmit(onSubmitForm)}>
                  <div className="flex flex-col gap-3 mb-6">
                    <ControlledReactSelect
                      control={control}
                      name="companyId"
                      label="Perusahaan"
                      menuPosition="absolute"
                      options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                      isLoading={isLoadingCompany}
                      isDisabled={isEdit}
                    />
                    <ControlledInput control={control} name="namaPIC" label="Nama PIC" isRequired={true} />
                    <ControlledInput control={control} name="jabatan" label="Jabatan" isRequired={true} />
                    <ControlledInput control={control} name="email" label="Email" isRequired={true} />
                    <ControlledInput control={control} name="telepon" label="Telepon" isRequired={true} />
                    <ControlledInput control={control} name="fax" label="Fax" isRequired={true} />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                      {isEdit ? "Simpan" : "Tambah"}
                    </Button>
                    <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
